import { Alert, Button, Paper } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useFeatureFlagsContext } from '../../contexts/featureFlagContext';
import { EmptyState } from '../EmptyState';
import { BrokerageCanvasserDirectoryFailureNotice } from '../BrokerageCanvasserDirectoryFailureNotice';
import { useBrokerageCanvasserDirectory } from '../BrokerageCanvasserDirectoryContext';
import { getDefaultCustomDateRange } from '../filters/calendarDateRange';
import { ErrorState } from '../ErrorState';
import { LoadingState } from '../LoadingState';
import { PageHeader } from '../PageHeader';
import { PageControlsBar } from '../PageControlsBar';
import { CanvassingSessionBulkTranscription } from './CanvassingSessionBulkTranscription';
import { CanvassingSessionExport } from './CanvassingSessionExport';
import type { CanvassingSessionFetchImpl } from './canvassingSessionApi';
import { CanvassingSessionFilterBar } from './CanvassingSessionFilterBar';
import {
  canvassingSessionFiltersToSearchParams,
  isCanvassingSessionCustomDateRangeValid,
  parseCanvassingSessionFilters,
  type CanvassingSessionDateRangePreset,
  type CanvassingSessionFilterState,
} from './canvassingSessionFilters';
import { CanvassingSessionTable } from './CanvassingSessionTable';
import { useCanvassingSessionFilterOptions } from './useCanvassingSessionFilterOptions';
import { useCanvassingSessionList } from './useCanvassingSessionList';

export type CanvassingSessionsPageProps = {
  fetchImpl?: CanvassingSessionFetchImpl;
};

export const CanvassingSessionsPage = ({ fetchImpl }: CanvassingSessionsPageProps) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = parseCanvassingSessionFilters(searchParams);
  const [customDraft, setCustomDraft] = useState<{ fromDate: string; throughDate: string } | null>(null);
  const appliedDateKey = JSON.stringify([filters.dateRange, filters.fromDate, filters.throughDate]);
  const previousAppliedDateKey = useRef(appliedDateKey);
  useEffect(() => {
    if (previousAppliedDateKey.current === appliedDateKey) return;
    previousAppliedDateKey.current = appliedDateKey;
    if (
      customDraft !== null &&
      (filters.dateRange !== 'custom' ||
        customDraft.fromDate !== (filters.fromDate ?? '') ||
        customDraft.throughDate !== (filters.throughDate ?? ''))
    ) {
      setCustomDraft(null);
    }
  }, [appliedDateKey, customDraft, filters.dateRange, filters.fromDate, filters.throughDate]);

  const dateRange: CanvassingSessionDateRangePreset = customDraft === null ? filters.dateRange : 'custom';
  const customFromDate = customDraft?.fromDate ?? filters.fromDate ?? '';
  const customThroughDate = customDraft?.throughDate ?? filters.throughDate ?? '';
  const listFilters =
    customDraft === null
      ? filters
      : {
          ...filters,
          dateRange: 'custom' as const,
          fromDate: customFromDate || undefined,
          throughDate: customThroughDate || undefined,
        };
  const customRangeInvalid =
    dateRange === 'custom' && !isCanvassingSessionCustomDateRangeValid(customFromDate || undefined, customThroughDate || undefined);
  const list = useCanvassingSessionList({ fetchImpl, filters: listFilters, enabled: !customRangeInvalid });
  const filterOptions = useCanvassingSessionFilterOptions({ fetchImpl });
  const canvasserDirectory = useBrokerageCanvasserDirectory();
  const { isStructurelyAdmin } = useFeatureFlagsContext();
  const returnTo = `${location.pathname}${location.search}`;

  const handleFiltersChange = (nextFilters: CanvassingSessionFilterState): void => {
    setSearchParams(canvassingSessionFiltersToSearchParams(nextFilters), { replace: true });
  };

  const handleDateRangeChange = (nextDateRange: CanvassingSessionDateRangePreset): void => {
    if (nextDateRange === 'custom') {
      const range = filters.dateRange === 'custom'
        ? { fromDate: filters.fromDate ?? '', throughDate: filters.throughDate ?? '' }
        : getDefaultCustomDateRange(new Date(), list.calendarTimeZone);
      setCustomDraft(null);
      setSearchParams(
        canvassingSessionFiltersToSearchParams({ ...filters, dateRange: 'custom', ...range }),
        { replace: true },
      );
      return;
    }

    setCustomDraft(null);
    setSearchParams(
      canvassingSessionFiltersToSearchParams({ ...filters, dateRange: nextDateRange, fromDate: undefined, throughDate: undefined }),
      { replace: true },
    );
  };

  const handleCustomRangeChange = (fromDate: string, throughDate: string): void => {
    const draft = { fromDate, throughDate };
    setCustomDraft(draft);
    if (isCanvassingSessionCustomDateRangeValid(fromDate || undefined, throughDate || undefined)) {
      setSearchParams(
        canvassingSessionFiltersToSearchParams({ ...filters, dateRange: 'custom', fromDate, throughDate }),
        { replace: true },
      );
    }
  };

  const listErrorTitle = list.error?.kind === 'forbidden' ? 'Permission required' : undefined;
  const sessionActionsDisabled = customRangeInvalid || list.loading || list.error !== null || !(list.total > 0);
  const filterOptionsMessage =
    filterOptions.error?.kind === 'forbidden'
      ? 'You do not have permission to load canvasser filter options.'
      : "Couldn't load filter options. Any applied canvasser filter is still in effect.";

  return (
    <>
      <PageHeader title="Canvassing Sessions" />
      {isStructurelyAdmin && (
        <PageControlsBar label="Session actions">
          <CanvassingSessionBulkTranscription
            filters={listFilters}
            listTotal={list.total}
            disabled={sessionActionsDisabled}
            fetchImpl={fetchImpl}
            resolveCanvasserLabel={canvasserDirectory.resolveLabel}
          />
          <CanvassingSessionExport
            filters={listFilters}
            listTotal={list.total}
            disabled={sessionActionsDisabled}
            fetchImpl={fetchImpl}
            resolveCanvasserLabel={canvasserDirectory.resolveLabel}
          />
        </PageControlsBar>
      )}
      <Paper variant="outlined">
        <CanvassingSessionFilterBar
          filters={filters}
          onChange={handleFiltersChange}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          customFromDate={customFromDate}
          customThroughDate={customThroughDate}
          onCustomRangeChange={handleCustomRangeChange}
          calendarTimeZone={list.calendarTimeZone}
          canvasserOptions={filterOptions.canvasserIds}
          resolveCanvasserLabel={canvasserDirectory.resolveLabel}
        />
        <BrokerageCanvasserDirectoryFailureNotice />
        {filterOptions.error !== null && (
          <Alert
            severity="warning"
            sx={{ mx: 2, mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={filterOptions.refetch}>
                Retry
              </Button>
            }
          >
            {filterOptionsMessage}
          </Alert>
        )}
        {!customRangeInvalid && list.loading && <LoadingState />}
        {!customRangeInvalid && !list.loading && list.error !== null && (
          <ErrorState title={listErrorTitle} message={list.error.message} onRetry={list.refetch} />
        )}
        {!customRangeInvalid && !list.loading && list.error === null && list.items.length === 0 && (
          <EmptyState title="No canvassing sessions match these filters." />
        )}
        {!customRangeInvalid && !list.loading && list.error === null && list.items.length > 0 && (
          <CanvassingSessionTable
            items={list.items}
            total={list.total}
            page={list.page}
            rowsPerPage={list.rowsPerPage}
            returnTo={returnTo}
            canvasserDirectoryStatus={canvasserDirectory.status}
            resolveCanvasserLabel={canvasserDirectory.resolveLabel}
            onPageChange={list.setPage}
            onRowsPerPageChange={list.setRowsPerPage}
          />
        )}
      </Paper>
    </>
  );
};
